import type { ComponentProps } from 'react';
import { useMemo } from 'react';
import type { ThemeSpaces } from '@frameio/vapor';
import { CircularProgress, theme } from '@frameio/vapor';
import { IconWarningFill, IconXCircleFill } from '@frameio/vapor-icons';
import type { AssetState } from '../types';

type Props = {
  /** The current visual status */
  assetState: AssetState;
  /** The size of the circle.
   *
   * xsmall-compact will only render the icon in error
   * modes, not the circle */
  size: 'xsmall' | 'small' | 'medium' | 'xsmall-compact';
  /** The current percentage of the upload completion */
  percentage: number;
};

type ConfigSizes = {
  [key in Props['size']]: {
    showIconWarning: boolean;
    size: ThemeSpaces;
    strokeWidth: ThemeSpaces;
  };
};

/**
 * Render a circular progress indicator that animates between asset states.
 */
export function AssetCircularProgress({ assetState, size, percentage }: Props) {
  const propsForState = useMemo(() => {
    switch (assetState) {
      case 'queued':
        return {
          label: false,
          background: true,
          color: 'transparent',
          animateValue: false,
          value: 0,
        };

      case 'firefly-generating':
      case 'generating-thumbnail':
        return {
          label: false,
          background: true,
          color: 'transparent',
          animateValue: false,
          value: percentage,
        };

      case 'encoding':
      case 'uploading':
        return {
          label: true,
          value: percentage,
          background: false,
          color: 'brand',
        };

      case 'paused':
        return {
          label: size !== 'small' && percentage !== 0,
          background: false,
          value: percentage,
          color: 'neutral',
          labelOpacity: 0.7,
        };

      case 'interactive':
      case 'preparing':
      case 'complete':
        return {
          label: false,
          background: false,
          value: percentage,
          color: 'transparent',
          trackBackgroundColor: 'transparent',
          labelOpacity: 0,
        };

      case 'firefly-error':
        return {
          label: config[size].showIconWarning && renderErrorIcon,
          background: true,
          color: 'error',
          labelOpacity: 1,
          value: 100,
          animateValue: percentage && percentage > 0,
          strokeWidth: size === 'xsmall-compact' ? 0 : 0.5,
        };

      case 'error':
        return {
          label: config[size].showIconWarning && renderWarningIcon,
          background: true,
          color: 'error',
          labelOpacity: 1,
          value: 100,
          animateValue: percentage && percentage > 0,
          strokeWidth: size === 'xsmall-compact' ? 0 : 2,
        };

      case 'remote':
      case 'external':
        return {
          label: false,
          background: true,
          color: 'transparent',
          trackBackgroundColor: theme.colors.background.interactive.brandLow,
          animateValue: false,
          value: 0,
        };

      default:
        return {};
    }
  }, [assetState, percentage, size]);

  const circleProps = {
    track: true,
    color: 'neutral',
    strokeWidth: config[size].strokeWidth,
    trackBackgroundColor: theme.colors.border.tertiary,
    ...propsForState,
  } as ComponentProps<typeof CircularProgress>;

  if (size !== 'medium') {
    circleProps.label = false;
  }

  return <CircularProgress size={config[size].size} {...circleProps} />;
}

const renderWarningIcon = () => (
  <IconWarningFill color={theme.colors.icon.interactive.error} />
);

const renderErrorIcon = () => (
  <IconXCircleFill color={theme.colors.icon.interactive.error} />
);

const config: ConfigSizes = {
  xsmall: {
    showIconWarning: false,
    size: 16,
    strokeWidth: 2,
  },
  'xsmall-compact': {
    showIconWarning: true,
    size: 16,
    strokeWidth: 2,
  },
  small: {
    showIconWarning: false,
    size: 32,
    strokeWidth: 4,
  },
  medium: {
    showIconWarning: true,
    size: 64,
    strokeWidth: 4,
  },
};
